import { Avatar, Group, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	useDeleteAiTask,
	useListAiTasks,
	useRunAiTask,
	type AiTaskOut,
} from '@repo/api-codegen';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import { UserRole } from '@repo/common/enums/UserRole';

import { Breadcrumbs, Button, Icon, Text } from '@repo/foundations';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router';
import { queryClient } from '../../api';
import { EmptyState } from '../../components/EmptyState';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { FullWidthLoadingSpinner } from '../../components/LoadingSpinner';

import { TableV2 } from '../../components/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

import {
	NavBarBreadcrumbWrapper,
	NavBarRightSideWrapper,
	NavBarWrapper,
} from '../../components/SecodaAppShell/SecodaNavBar';

import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import { useAiWorkspaceSettingEnabled } from '../../hooks/useAiWorkspaceSettingEnabled';
import { NotificationMessages } from './constants';
import { CreateAgentModal } from './CreateAgentModal';
import {
	getHumanReadableSchedule,
	invalidateAgentRunsCache,
	invalidateChatHistoryCache,
} from './utils';

/**
 * Standardized error notification helper
 */
const showErrorNotification = (message: { title: string; message: string }) => {
	showNotification({
		...message,
		color: 'red',
		icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
	});
};

/**
 * Standardized success notification helper
 */
const showSuccessNotification = (message: {
	title: string;
	message: string;
}) => {
	showNotification({
		...message,
		color: 'green',
		icon: <Icon name="check" color="text/success-on-fill/default" />,
	});
};

const RunButtonCell = React.memo(({ agent }: { agent: AiTaskOut }) => {
	const navigate = useNavigate();

	const { mutateAsync: runAgent, isPending: isRunning } = useRunAiTask({
		onSuccess: () => {
			queryClient.invalidateQueries();
			showSuccessNotification(NotificationMessages.AGENT_EXECUTED);
		},
	});

	const handleRun = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			e.preventDefault();

			let result;
			try {
				result = await runAgent({ pathParams: { taskId: agent.id } });
			} catch {
				showErrorNotification(NotificationMessages.AGENT_RUN_ERROR);
				return;
			}

			if (result.embedded_prompt_id) {
				await Promise.all([
					invalidateChatHistoryCache(),
					invalidateAgentRunsCache(agent.id),
				]);
				navigate(`/ai/${result.embedded_prompt_id}`);
			}
		},
		[runAgent, navigate, agent.id]
	);

	return (
		<Button variant="default" onClick={handleRun} loading={isRunning}>
			Run now
		</Button>
	);
});

const useColumns = (): ExtendedDataTableColumn<AiTaskOut>[] =>
	useMemo(
		() => [
			{
				accessor: 'title',
				titleLabel: 'Name',
				render: (record: AiTaskOut) => (
					<Text size="sm" fw="semibold" truncate>
						{record.title}
					</Text>
				),
			},
			{
				accessor: 'persona',
				titleLabel: 'Persona',
				render: (record: AiTaskOut) => (
					<Group spacing="2xs" noWrap>
						{record.persona?.avatar_url ? (
							<Avatar
								src={record.persona.avatar_url}
								size="sm"
								radius="xs"
								alt={`${record.persona.name} avatar`}
							/>
						) : (
							<AIAvatar size="sm" speed="none" iconSpeed="none" />
						)}
						<Text size="sm" c="text/primary/subdued" truncate>
							{!!record.persona
								? record.persona.name || 'Unnamed persona'
								: 'Default Secoda AI'}
						</Text>
					</Group>
				),
			},
			{
				accessor: 'cron_expression',
				titleLabel: 'Schedule',
				render: (record: AiTaskOut) => (
					<Group spacing="2xs" noWrap>
						<Icon
							name={record.cron_expression ? 'clock' : 'playerPlay'}
							size="md"
						/>
						<Text size="sm" c="text/primary/subdued" truncate>
							{
								getHumanReadableSchedule('cron', record.cron_expression || '')
									.humanReadableSchedule
							}
						</Text>
					</Group>
				),
			},
			{
				accessor: 'last_run',
				titleLabel: 'Last Run',
				render: (record: AiTaskOut) => (
					<Group spacing="2xs" noWrap>
						<Icon name="history" size="md" />
						<Text size="sm" c="text/primary/subdued">
							{record.last_run
								? dayjs(record.last_run).format('MMM DD, YYYY')
								: 'Never'}
						</Text>
					</Group>
				),
			},
			{
				accessor: 'next_run',
				titleLabel: 'Next Run',
				render: (record: AiTaskOut) => (
					<Group spacing="2xs" noWrap>
						<Icon name="calendar" size="md" />
						<Text size="sm" c="text/primary/subdued">
							{record.next_run
								? dayjs(record.next_run).format('MMM DD, YYYY')
								: '-'}
						</Text>
					</Group>
				),
			},
			{
				accessor: '',
				titleLabel: '',
				navigate: false,
				render: (record: AiTaskOut) => (
					<Group spacing="sm" noWrap position="right">
						<RunButtonCell agent={record} />
					</Group>
				),
			},
		],
		[]
	);

const useActions = (): ICommandListItem<AiTaskOut>[] => {
	const navigate = useNavigate();
	const { mutateAsync: deleteAgent } = useDeleteAiTask({
		onSuccess: () => {
			queryClient.invalidateQueries();
			showSuccessNotification(NotificationMessages.AGENT_DELETED);
		},
	});

	const { mutateAsync: runAgent } = useRunAiTask({
		onSuccess: () => {
			queryClient.invalidateQueries();
			showSuccessNotification(NotificationMessages.AGENT_EXECUTED);
		},
	});

	const handleDeleteAgents = useCallback(
		async (agents: AiTaskOut[], clearSelected: VoidFunction) => {
			try {
				const results = await Promise.allSettled(
					agents.map((agent) =>
						deleteAgent({ pathParams: { taskId: agent.id } })
					)
				);

				const success = results.filter(
					(result) => result.status === 'fulfilled'
				);
				const failed = results.filter((result) => result.status === 'rejected');

				// Invalidate queries to refresh the table
				queryClient.invalidateQueries();

				if (failed.length > 0) {
					showErrorNotification({
						title: `Error deleting ${failed.length} agent${failed.length > 1 ? 's' : ''}`,
						message: 'If the issue persists, please contact support',
					});
				} else {
					showSuccessNotification({
						title: `${success.length} agent${success.length > 1 ? 's' : ''} deleted`,
						message: `The agent${success.length > 1 ? 's have' : ' has'} been deleted successfully.`,
					});
				}

				clearSelected();
			} catch {
				showErrorNotification(NotificationMessages.AGENT_DELETE_ERROR);
			}
		},
		[deleteAgent]
	);

	const handleRunAgents = useCallback(
		async (agents: AiTaskOut[]) => {
			let results;
			try {
				results = await Promise.all(
					agents.map((agent) => runAgent({ pathParams: { taskId: agent.id } }))
				);
			} catch {
				showErrorNotification(NotificationMessages.AGENT_RUN_ERROR);
				return;
			}

			const firstResult = results.find((result) => result.embedded_prompt_id);
			if (firstResult?.embedded_prompt_id) {
				await Promise.all([
					invalidateChatHistoryCache(),
					...agents.map((agent) => invalidateAgentRunsCache(agent.id)),
				]);
				navigate(`/ai/${firstResult.embedded_prompt_id}`);
			}
		},
		[runAgent, navigate]
	);

	const actions = useMemo(
		() => [
			{
				id: 'actions::run',
				title: 'Run now',
				name: 'Run',
				iconName: 'playerPlay' as const,
				type: 'ai_task',
				team: undefined,
				category: 'actions',
				show: true,
				onClick: handleRunAgents,
			},
			{
				id: 'actions::delete',
				title: 'Delete',
				name: 'Delete',
				iconName: 'trash' as const,
				type: 'ai_task',
				team: undefined,
				category: 'actions',
				show: true,
				onClick: handleDeleteAgents,
			},
		],
		[handleDeleteAgents, handleRunAgents]
	);

	return actions;
};

/**
 * Main page component for managing AI Agents
 *
 * Provides functionality to:
 * - View and list all AI agents in a table
 * - Create new AI agents with custom prompts and schedules
 * - Edit existing agents
 * - Run agents manually or view scheduled runs
 * - Delete agents with confirmation
 *
 * Features:
 * - Cron expression scheduling with human-readable descriptions
 * - Persona selection for different AI behaviors
 * - Web search capability toggle
 * - Rich text editor for prompts
 * - Run history tracking
 * - Responsive design with drawer-based editing
 *
 * @returns {JSX.Element} The AI Agents page component
 */
function AIAgentsPage() {
	const enableAi = useAiWorkspaceSettingEnabled();
	const actions = useActions();
	const [modalOpened, setModalOpened] = useState(false);
	const [editingAgent, setEditingAgent] = useState<AiTaskOut | null>(null);

	const { data: agents, isLoading } = useListAiTasks({ queryParams: {} });

	const handleCloseModal = useCallback(() => {
		setModalOpened(false);
		setEditingAgent(null);
	}, []);

	// Initialize columns
	const columns = useColumns();

	const handleOpenModal = useCallback((agent?: AiTaskOut) => {
		if (agent) {
			setEditingAgent(agent);
		} else {
			setEditingAgent(null);
		}
		setModalOpened(true);
	}, []);

	const handleRowClick = useCallback(
		(agent: AiTaskOut) => {
			handleOpenModal(agent);
		},
		[handleOpenModal]
	);

	if (!enableAi) {
		return (
			<Stack spacing={0}>
				<Helmet>
					<title>AI Agents</title>
				</Helmet>

				<NavBarWrapper>
					<NavBarBreadcrumbWrapper>
						<Breadcrumbs
							crumbs={[
								{ title: 'Secoda AI', href: '/ai' },
								{ title: 'Agents', href: '/ai/agents' },
							]}
						/>
					</NavBarBreadcrumbWrapper>
				</NavBarWrapper>

				<Stack h="100%" justify="center">
					<EmptyState
						illustrationName="ai"
						title="Secoda AI is disabled"
						description="Secoda AI is not enabled for this workspace. Please contact your administrator to turn on access."
						includeGoBack={false}
						stateHeight="80vh"
						size="lg"
					/>
				</Stack>
			</Stack>
		);
	}

	return (
		<FeatureGuard
			featureName="AI Agents"
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR, UserRole.VIEWER]}
			v2Permission="SecodaAI.Read"
			description="Create and manage AI agents to automate your data workflows."
			isFeatureIncludedInPlan={enableAi}
			isAlwaysIncludedFeature={true}
		>
			<Stack spacing={0}>
				<Helmet>
					<title>AI Agents</title>
				</Helmet>

				<NavBarWrapper>
					<NavBarBreadcrumbWrapper>
						<Breadcrumbs
							crumbs={[
								{ title: 'Secoda AI', href: '/ai/new' },
								{ title: 'Agents', href: '/ai/agents' },
							]}
						/>
					</NavBarBreadcrumbWrapper>
					<NavBarRightSideWrapper>
						<Button onClick={() => handleOpenModal()} variant="primary">
							Create agent
						</Button>
					</NavBarRightSideWrapper>
				</NavBarWrapper>

				<Stack h="100%">
					{isLoading && <FullWidthLoadingSpinner />}
					{!isLoading && !agents?.results?.length && (
						<EmptyState
							illustrationName="ai"
							title="No agents yet"
							description="Create your first AI agent to get started with automating your data workflows."
							includeGoBack={false}
							size="lg"
						/>
					)}
					{!isLoading && !!agents?.results?.length && (
						<Stack p="lg">
							<TableV2
								columns={columns}
								// @ts-expect-error using API codegen list hook
								usePaginationList={useListAiTasks}
								usePaginationListOptions={{ queryParams: {} }}
								onRowClick={handleRowClick}
								pluralTypeString="agents"
								tableCacheKey="ai-agents"
								withBorder
								useCodegenListInterface
								withActions={actions}
								withCheckbox={true}
								withQuickActions={['actions::run', 'actions::delete']}
							/>
						</Stack>
					)}
				</Stack>

				{modalOpened && (
					<CreateAgentModal onClose={handleCloseModal} agent={editingAgent} />
				)}
			</Stack>
		</FeatureGuard>
	);
}

export default AIAgentsPage;
